import { useState } from 'react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { Badge, Button, Caption1, MessageBar, MessageBarBody, Spinner, Text, makeStyles } from '@fluentui/react-components';
import { lifecycleStateSchema, type TimelineCommunication, type TimelineEntry, type TimelineStep } from '@vigeo/shared';
import { tokens, useSectionActive } from '@vigeo/ui';
import { lifecyclePresenters, statusDetailLine } from '@/features/candidates';
import { StoredFileLink } from '@/features/files';
import { fetchPersonTimeline, fetchTimelineMessage } from './api.js';
import { actorLabels, communicationStatusPresenters, entryTitle, entryWhen, interviewDetail, stepStatePresenters } from './presenters.js';

const useStyles = makeStyles({
  panel: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM, minWidth: 0 },
  list: { listStyleType: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS },
  row: {
    display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXS, minWidth: 0,
    paddingBlock: tokens.spacingVerticalS, borderBottom: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}`,
  },
  line: { display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: tokens.spacingHorizontalS, minWidth: 0, overflowWrap: 'anywhere' },
  body: { whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', paddingBlock: tokens.spacingVerticalS },
});

export interface TimelinePanelProps {
  readonly personId: string;
}

/**
 * The stored text as the row carries it. A long message arrives as a preview; its whole text
 * is fetched only when somebody asks for it (#195), and then shown in the preview's place.
 */
function MessageText({ personId, message }: { readonly personId: string; readonly message: TimelineCommunication }) {
  const styles = useStyles();
  const [open, setOpen] = useState(false);
  const full = useQuery({ queryKey: ['timeline-message', personId, message.id], queryFn: () => fetchTimelineMessage(personId, message.id), enabled: open });
  if (!message.textTruncated) return <div className={styles.body}>{message.text}</div>;
  if (full.data !== undefined) return <div className={styles.body}>{full.data.text}</div>;
  return <>
    <div className={styles.body}>{`${message.text}…`}</div>
    {!open ? <div><Button appearance="subtle" size="small" onClick={() => setOpen(true)}>Show the full message</Button></div>
      : full.isError ? <MessageBar intent="error"><MessageBarBody>{full.error.message}</MessageBarBody></MessageBar>
        : <Spinner size="tiny" label="Loading the full message" />}
  </>;
}

function CommunicationDetail({ personId, message }: { readonly personId: string; readonly message: TimelineCommunication }) {
  const styles = useStyles();
  return <>
    <div className={styles.line}>
      <Text>{message.subject}</Text>
      <Badge appearance="outline" size="medium" shape="rounded">{communicationStatusPresenters[message.status].label}</Badge>
    </div>
    {message.reason === null ? null : <Caption1>{message.reason}</Caption1>}
    <details>
      <summary>Message as recorded</summary>
      <MessageText personId={personId} message={message} />
      {message.redacted ? <Caption1>The link and access code were removed from this stored copy.</Caption1> : null}
    </details>
  </>;
}

function StepDetail({ personId, step }: { readonly personId: string; readonly step: TimelineStep }) {
  const styles = useStyles();
  return <div className={styles.line}>
    <Text>{step.heading}</Text>
    <Badge appearance="outline" size="medium" shape="rounded">{stepStatePresenters[step.state].label}</Badge>
    {step.adHoc ? <Badge appearance="outline" size="small">One-off</Badge> : null}
    {step.segregated ? <Badge appearance="outline" size="small">Separate file</Badge> : null}
    {step.storedFileId !== null || step.fileUrl !== null ? <StoredFileLink personId={personId} storedFileId={step.storedFileId} legacyUrl={step.fileUrl} label="Open file" />
      : step.fileWithheld ? <Caption1>File available to HR admins</Caption1> : null}
  </div>;
}

/** Stage names read as the pipeline shows them; any other transition keeps its stored words. */
function transitionLabel(entry: Extract<TimelineEntry, { kind: 'event' }>, value: string): string {
  if (entry.action !== 'person.lifecycle-changed') return value;
  const parsed = lifecycleStateSchema.safeParse(value);
  return parsed.success ? lifecyclePresenters[parsed.data].label : value;
}

function Row({ personId, entry }: { readonly personId: string; readonly entry: TimelineEntry }) {
  const styles = useStyles();
  const when = entryWhen(entry);
  const event = entry.kind === 'event' ? entry : null;
  const message = entry.kind === 'communication' ? entry.communication : event?.communication ?? null;
  const step = entry.kind === 'step' ? entry.step : event?.step ?? null;
  const interview = entry.kind === 'interview' ? interviewDetail(entry) : '';
  return <li className={styles.row}>
    <Caption1>{event === null ? when : `${when} · ${actorLabels[event.actorType]}`}</Caption1>
    <Text weight="semibold">{entryTitle(entry)}</Text>
    {event !== null && event.from !== null && event.to !== null ? <Caption1>{transitionLabel(event, event.from)} → {transitionLabel(event, event.to)}</Caption1> : null}
    {event !== null && event.statusDetail !== null && statusDetailLine(event.statusDetail) !== null ? <Caption1>{statusDetailLine(event.statusDetail)}</Caption1> : null}
    {event !== null && event.reason !== null && message === null ? <Caption1>{event.reason}</Caption1> : null}
    {interview === '' ? null : <Caption1>{interview}</Caption1>}
    {message === null ? null : <CommunicationDetail personId={personId} message={message} />}
    {step === null ? null : <StepDetail personId={personId} step={step} />}
  </li>;
}

/**
 * Everything that happened to one person, newest first, a page at a time. Read-only, derived
 * by the API on every request (#62): no row here is stored, edited or deleted, ever. Older
 * rows load on request (#195) from the cursor the last page returned.
 */
export function TimelinePanel({ personId }: TimelinePanelProps) {
  const styles = useStyles();
  const active = useSectionActive();
  const timeline = useInfiniteQuery({
    queryKey: ['timeline', personId],
    queryFn: ({ pageParam }) => fetchPersonTimeline(personId, pageParam),
    initialPageParam: null as string | null,
    getNextPageParam: (page) => page.nextCursor,
    enabled: active,
  });

  if (timeline.isPending) return <Spinner size="small" label="Loading the timeline" />;
  // A failed OLDER page leaves the query in error with the loaded pages kept: show those, and say so below them.
  if (timeline.data === undefined) return <MessageBar intent="error"><MessageBarBody>{timeline.error?.message ?? 'The timeline could not be loaded.'}</MessageBarBody></MessageBar>;
  const entries = timeline.data.pages.flatMap((page) => page.entries);
  return <section className={styles.panel} aria-label="Timeline">
    <Caption1>Messages, requests and record changes, newest first. Built from the record each time it is opened.</Caption1>
    {entries.length === 0 ? <Text>Nothing has happened on this record yet.</Text>
      : <ol className={styles.list}>{entries.map((entry) => <Row key={entry.id} personId={personId} entry={entry} />)}</ol>}
    {timeline.isFetchNextPageError ? <MessageBar intent="error"><MessageBarBody>{timeline.error?.message ?? 'Older entries could not be loaded.'}</MessageBarBody></MessageBar> : null}
    {timeline.hasNextPage
      ? <div><Button appearance="secondary" disabled={timeline.isFetchingNextPage} onClick={() => void timeline.fetchNextPage()}>
        {timeline.isFetchingNextPage ? 'Loading older entries' : 'Show older entries'}
      </Button></div>
      : null}
  </section>;
}
